import { All, AuthActionTypes } from '../actions/auth.actions';
import jwt_decode from "jwt-decode";
import { isNullOrUndefined } from 'is-what';
import { DecodeAccessToken, Identities } from './models';
import { SiteName } from '@app-shared/models/routingdata';
import { EncryptedFhlBankUserModel } from '@empf-shared/services/treasury/models/EncryptedFhlBankUserModel';
import { DecodeAzureB2CIDToken } from './models/DecodeAzureB2CIDToken';
import { DecodeAzureB2CAccessToken } from './models/DecodeAzureB2CAccessToken';
import { Behavior } from './models/Behavior';
import { fhlbConstants } from '@app-shared/constants/fhlb-constants';
import { AppConfigService } from '@app-shared/services';
import { LoginFlow } from '@app-shared/constants/login-flow';
import { AuthData } from './models/AuthData';

const INIT_ACTION = '@ngrx/store/init';

export interface State {
  site?: SiteName,
  loginFlow?: LoginFlow,
  isRefreshOccurring: boolean | null
  encoded: {
    azureB2CIDToken: string;
    azureB2cAccessToken: string;
  }
  decoded: {
    idToken: DecodeAccessToken;
    azureB2CIDToken: DecodeAzureB2CIDToken;
    azureB2cAccessToken: DecodeAzureB2CAccessToken;
  }
  azureB2cTokenExpireDateTime?: Date;
  empfApplicationExpireDateTime?: Date;
  empfApplicationShowRefreshModalDateTime?: Date;
  hlbBankInfo: EncryptedFhlBankUserModel;
  selectedProfileScopeID: string;
}

export const initialState: State = {
  site: null,
  loginFlow: LoginFlow.NO_FLOW,
  isRefreshOccurring: null,
  encoded: {
    azureB2CIDToken: null,
    azureB2cAccessToken: null,
  },
  decoded: {
    idToken: null,
    azureB2CIDToken: null,
    azureB2cAccessToken: null,
  },
  azureB2cTokenExpireDateTime: null,
  empfApplicationExpireDateTime: null,
  empfApplicationShowRefreshModalDateTime: null,
  hlbBankInfo: null,
  selectedProfileScopeID: null
};

export function reducer(state = initialState, action: All): State {
  let entryLogging: boolean = false;
  if (AppConfigService?.Settings?.App?.Logging?.B2CEntryLogging) entryLogging = true;

  switch (action.type) {
    case AuthActionTypes.LOGIN_SUCCESS: {
      let decodedAzureB2CIDToken: DecodeAzureB2CIDToken = JSON.parse(JSON.stringify(action.payload.decodedAzureB2CIDToken));
      decodedAzureB2CIDToken.authData = parseAuthDataString(decodedAzureB2CIDToken.authData);

      let newState: State = {
        site: state.site,
        loginFlow: state.loginFlow,
        isRefreshOccurring: false,
        encoded: {
          azureB2CIDToken: action.payload.encodedAzureB2CIDtoken,
          azureB2cAccessToken: null
        },
        decoded: {
          idToken: null,
          azureB2CIDToken: decodedAzureB2CIDToken,
          azureB2cAccessToken: null
        },
        azureB2cTokenExpireDateTime: new Date(action.payload.decodedAzureB2CIDToken.exp * 1000),
        empfApplicationExpireDateTime: null,
        empfApplicationShowRefreshModalDateTime: null,
        hlbBankInfo: state.hlbBankInfo,
        selectedProfileScopeID: null
      }
      return newState;
    }
    case AuthActionTypes.REFRESH_TOKENS_SUCCESS: {
      let decodeAzureB2CAccessToken = decodeAzureB2CAccessJwtToken(action.payload.encodedAzureB2CAccessToken)
      let decodeAzureB2CIDToken = decodeAzureB2CIDJwtToken(action.payload.encodedAzureB2CIDToken);
      
      let identity: Identities[] = [{
        userId: state.decoded.idToken.identities[0].userId,
        providerName: ''
      }]


      let decodedToken: DecodeAccessToken = {
        'cognito:groups': null,
        'cognito:username': null,
        'custom:UserGroup': state.decoded.idToken['custom:UserGroup'],
        'custom:OrgKey': state.decoded.idToken['custom:OrgKey'],
        'custom:Behavior': state.decoded.idToken['custom:Behavior'],
        email: decodeAzureB2CIDToken.email,
        exp: decodeAzureB2CIDToken.exp,
        given_name: decodeAzureB2CIDToken.given_name,
        iat: decodeAzureB2CIDToken.iat,
        identities: identity
      }
      let newEmpfAppExpireDateTime= calculateExpireTimeStamp(decodedToken['custom:UserGroup'],decodedToken.iat, entryLogging);
      let newEmpfAppShowRefreshModalDateTime = calculateShowRefreshModalTimeStamp(decodedToken['custom:UserGroup'], newEmpfAppExpireDateTime, entryLogging);
      let newState: State = {
        site: state.site,
        loginFlow: (action.payload.loginFlow) ? action.payload.loginFlow : state.loginFlow,
        isRefreshOccurring: state.isRefreshOccurring,
        encoded: {
          azureB2CIDToken: action.payload.encodedAzureB2CIDToken,
          azureB2cAccessToken: action.payload.encodedAzureB2CAccessToken
        },
        decoded: {
          idToken: decodedToken,
          azureB2CIDToken: decodeAzureB2CIDToken,
          azureB2cAccessToken: decodeAzureB2CAccessToken
        },
        azureB2cTokenExpireDateTime:  new Date(decodeAzureB2CIDToken.exp * 1000),              
        empfApplicationExpireDateTime: (action.payload.updateEmpfSessionTime) ? newEmpfAppExpireDateTime
         : state.empfApplicationExpireDateTime,
        empfApplicationShowRefreshModalDateTime:(action.payload.updateEmpfSessionTime) ? newEmpfAppShowRefreshModalDateTime
        : state.empfApplicationShowRefreshModalDateTime  ,
        hlbBankInfo: state.hlbBankInfo,
        selectedProfileScopeID: state.selectedProfileScopeID
      }      
      return newState;
    }
    case AuthActionTypes.SET_REFRESH_STATUS: {
      return { ...state, isRefreshOccurring: action.payload};
    }

    case AuthActionTypes.REFRESH_TOKENS_FAIL: {
      return { ...state };
    }
    case AuthActionTypes.SET_SITE: {
      return { ...state, site: action.payload };
    }

    case AuthActionTypes.LOG_OUT: {
      return { ...initialState };
    }
    case AuthActionTypes.LOG_OUT_LOCAL: {
      return { ...initialState };
    }
    case AuthActionTypes.LOG_OUT_ACKNOWLEDGE: {
      return { ...state };
    }
    case AuthActionTypes.HLB_BANK_LOGIN: {
      return { ...state, hlbBankInfo: action.payload };
    }
    case AuthActionTypes.USER_PROFILE_SELECTED: {
      if (entryLogging) console.log("selected scope id :", action.payload.selectedProfile);

      let identity: Identities[] = [{
        userId: action.payload.userProfile.NtUserName,
        providerName: ''
      }];

      let decodedToken: DecodeAccessToken = {
        'cognito:groups': null,
        'cognito:username': null,
        'custom:UserGroup': action.payload.userProfile.Roles,
        'custom:OrgKey': action.payload.userProfile.OrgKey.toString(),
        'custom:Behavior': mapBehaviorToDescriptions(action.payload.userProfile.Behaviors),
        email: null,
        exp: 0,
        given_name: null,
        iat: 0,
        identities: identity
      };
      let newState: State = {
        site: state.site,
        loginFlow: state.loginFlow,
        isRefreshOccurring: false,
        encoded: {
          azureB2CIDToken: null,
          azureB2cAccessToken: null
        },
        decoded: {
          idToken: decodedToken,
          azureB2CIDToken: state.decoded.azureB2CIDToken,
          azureB2cAccessToken: null
        },
        azureB2cTokenExpireDateTime: state.azureB2cTokenExpireDateTime,
        empfApplicationExpireDateTime: null,
        empfApplicationShowRefreshModalDateTime: null,
        hlbBankInfo: state.hlbBankInfo,
        selectedProfileScopeID: action.payload.selectedProfile
      };
      if (entryLogging) console.log("selected Profile reducer values: ", newState);
      return newState;

    }
    case AuthActionTypes.USER_PROFILE_CLEARED: {
      return { ...initialState };
    }
    case AuthActionTypes.REFRESH_TOKENS:{
      return {...state};
    }
    default: {
      return state;
    }
  }
}

export const rehydrateReducer = (state: any, rehydratedState: any, action: any) => {
  if (action.type === INIT_ACTION && rehydratedState && Object.keys(rehydratedState).length > 0) {
        
    let rehydratedStateTyped: State = rehydratedState.auth as State;
    let decodeAzureB2CAccessToken = decodeAzureB2CAccessJwtToken(rehydratedStateTyped?.encoded?.azureB2cAccessToken)
    let decodeAzureB2CIDToken = decodeAzureB2CIDJwtToken(rehydratedStateTyped?.encoded?.azureB2CIDToken);
    if (decodeAzureB2CIDToken && decodeAzureB2CIDToken.authData && decodeAzureB2CIDToken.authData.UserProfiles && decodeAzureB2CIDToken.authData.UserProfiles.length > 0) {
      let userProfile = decodeAzureB2CIDToken.authData.UserProfiles.find(p => p.ProfileScopeID == rehydratedStateTyped.selectedProfileScopeID);
      if (userProfile) {
        let identity: Identities[] = [{
          userId: userProfile?.NtUserName,
          providerName: ''
        }];
        let decodedToken: DecodeAccessToken = {
          'cognito:groups': null,
          'cognito:username': null,
          'custom:UserGroup': userProfile?.Roles,
          'custom:OrgKey': userProfile?.OrgKey?.toString(),
          'custom:Behavior': mapBehaviorToDescriptions(userProfile?.Behaviors),
          email: decodeAzureB2CIDToken?.email,
          exp: decodeAzureB2CIDToken?.exp,
          given_name: decodeAzureB2CIDToken?.given_name,
          iat: decodeAzureB2CIDToken?.iat,
          identities: identity
        };
        let newEmpfAppExpireDateTime= calculateExpireTimeStamp(userProfile?.Roles, decodeAzureB2CIDToken.iat, false);
        let newEmpfAppShowRefreshModalDateTime = calculateShowRefreshModalTimeStamp(userProfile?.Roles, newEmpfAppExpireDateTime, false);

        let selectiveRehydrate: State = {
          site: rehydratedStateTyped?.site,
          loginFlow: rehydratedStateTyped?.loginFlow,
          isRefreshOccurring: rehydratedStateTyped?.isRefreshOccurring,
          encoded: {
            azureB2CIDToken: rehydratedStateTyped?.encoded?.azureB2CIDToken,
            azureB2cAccessToken: rehydratedStateTyped?.encoded?.azureB2cAccessToken
          },
          decoded: {
            idToken: decodedToken,
            azureB2CIDToken: decodeAzureB2CIDToken,
            azureB2cAccessToken: decodeAzureB2CAccessToken
          },
          azureB2cTokenExpireDateTime: new Date(decodeAzureB2CIDToken?.exp * 1000),              
          empfApplicationExpireDateTime: newEmpfAppExpireDateTime,
          empfApplicationShowRefreshModalDateTime: newEmpfAppShowRefreshModalDateTime,
          hlbBankInfo: rehydratedStateTyped?.hlbBankInfo,
          selectedProfileScopeID: rehydratedStateTyped?.selectedProfileScopeID
        };
        state = {
          auth: selectiveRehydrate
        };
      }
    }
  }
  return state;
};


function mapBehaviorToDescriptions(behaviors: number[]): string[] {
  if (behaviors) { return behaviors.map(behavior => Behavior[behavior]); }
  else return null;

}

function checkForExtendedTimeoutRole(roles: string[]): boolean
{
  let isExtendedTimeoutMpfRole: boolean = false;
  let validExtendedRoles: string[] = fhlbConstants.EmpfExtendedTimeoutMPFRole.map((function(roleString) { return roleString.toUpperCase(); } ));
  roles.forEach((role) =>
    {
      if (validExtendedRoles.includes(role.toUpperCase()))
      {
        isExtendedTimeoutMpfRole = true;
      }
    }); 
    
  return isExtendedTimeoutMpfRole;
}

function calculateExpireTimeStamp(roles :string[], issuedAt: number, logging: boolean) :Date
{
  let extendExpireMinutes = (AppConfigService?.Settings?.Empf?.ExtendedTimeoutRoleEmpfSessionExpirationInMins) ? AppConfigService?.Settings?.Empf?.ExtendedTimeoutRoleEmpfSessionExpirationInMins : 120;
  let expireMinutes = (AppConfigService?.Settings?.Empf?.EmpfSessionExpirationInMins) ? AppConfigService?.Settings?.Empf?.EmpfSessionExpirationInMins : 30;

  if (logging) console.log("calculateExpireTimeStamp Roles:", roles);
  let isExtendedTimeOutMpfRole: boolean = checkForExtendedTimeoutRole(roles);
  const issuedAtDate = new Date(issuedAt * 1000);
  if (logging) console.log("calculateExpireTimeStamp IssuedAt:", issuedAtDate);
  const newDate = new Date(issuedAtDate.getTime()); 

  if (isExtendedTimeOutMpfRole)
  {      
    newDate.setMinutes(newDate.getMinutes() + extendExpireMinutes);
    return newDate;
  }
  else {
    newDate.setMinutes(newDate.getMinutes() + expireMinutes);
    return newDate;
  }
}

function calculateShowRefreshModalTimeStamp(roles :string[], empfExp: Date, logging: boolean) :Date
{ 
  let extendRefreshMinutes = (AppConfigService?.Settings?.Empf?.ExtendedTimeoutRoleShowRefreshBeforeExpirationInMins) ? AppConfigService?.Settings?.Empf?.ExtendedTimeoutRoleShowRefreshBeforeExpirationInMins : 25;
  let refreshMinutes = (AppConfigService?.Settings?.Empf?.EmpfSessionShowRefreshBeforeExpirationInMins) ? AppConfigService?.Settings?.Empf?.EmpfSessionShowRefreshBeforeExpirationInMins : 5;

  if (logging) console.log("calculateShowRefreshModalTimeStamp empfExp", empfExp);
  let isExtendedTimeOutMpfRole: boolean = checkForExtendedTimeoutRole(roles);
  const newDate = new Date(empfExp.getTime());  

  if (isExtendedTimeOutMpfRole)
  {
    newDate.setMinutes(newDate.getMinutes() - extendRefreshMinutes);
    return newDate;
  }
  else {
    newDate.setMinutes(newDate.getMinutes() - refreshMinutes);
    return newDate;
  }
}

function decodeAzureB2CAccessJwtToken(token: string): DecodeAzureB2CAccessToken {
  if (isNullOrUndefined(token))
    return null;

  let ret: DecodeAzureB2CAccessToken
  const tkn = jwt_decode(token);

  ret = <DecodeAzureB2CAccessToken>tkn;
  ret.authData = parseAuthDataString(ret.authData);
  return ret;
}

function decodeAzureB2CIDJwtToken(token: string): DecodeAzureB2CIDToken {
  if (isNullOrUndefined(token))
    return null;

  let ret: DecodeAzureB2CIDToken
  const tkn = jwt_decode(token);

  ret = <DecodeAzureB2CIDToken>tkn;
  ret.authData = parseAuthDataString(ret.authData);
  return ret;
}

function parseAuthDataString(authDatastringParam: any): AuthData {
  let authDataString = (authDatastringParam as unknown) as string;
  let authData: AuthData;
  if (authDataString) {
    authData = JSON.parse(authDataString) as AuthData;
  }

  return authData;
}

